import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

// Async thunk to fetch user's additional details
export const fetchAdditionalDetails = createAsyncThunk(
  'additionalDetails/fetchAdditionalDetails',
  async (email, { rejectWithValue }) => {
    try {
      // Get document from additionalDetails collection using email as document ID
      const detailsDocRef = doc(db, 'additionalDetails', email);
      const detailsDocSnap = await getDoc(detailsDocRef);
      
      if (detailsDocSnap.exists()) {
        return {
          id: detailsDocSnap.id,
          ...detailsDocSnap.data()
        };
      } else {
        // Return empty details if document doesn't exist
        return {
          id: email,
          declarationConvictionDetails: '',
          declarationConvictionStatus: 'NO',
          declarationDisabilityDetails: '',
          declarationDisabilityStatus: 'NO',
          declarationHighestQualification: '',
          lastUpdated: null
        };
      }
    } catch (error) {
      console.error('Error fetching additional details:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to update user's additional details
export const updateAdditionalDetails = createAsyncThunk(
  'additionalDetails/updateAdditionalDetails',
  async (data, { rejectWithValue }) => {
    try {
      const { id, ...details } = data;
      
      // Add timestamp for lastUpdated
      const detailsWithTimestamp = {
        ...details,
        lastUpdated: new Date()
      };
      
      // Update document in additionalDetails collection
      await setDoc(doc(db, 'additionalDetails', id), detailsWithTimestamp, { merge: true });
      
      return {
        id,
        ...detailsWithTimestamp
      };
    } catch (error) {
      console.error('Error updating additional details:', error);
      return rejectWithValue(error.message);
    }
  }
);

const additionalDetailsSlice = createSlice({
  name: 'additionalDetails',
  initialState: {
    details: null,
    loading: false,
    updating: false,
    error: null
  },
  reducers: {
    clearAdditionalDetails: (state) => {
      state.details = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch additional details reducers
      .addCase(fetchAdditionalDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdditionalDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.details = action.payload;
      })
      .addCase(fetchAdditionalDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update additional details reducers
      .addCase(updateAdditionalDetails.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateAdditionalDetails.fulfilled, (state, action) => {
        state.updating = false;
        state.details = action.payload;
      })
      .addCase(updateAdditionalDetails.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload;
      });
  }
});

export const { clearAdditionalDetails } = additionalDetailsSlice.actions;
export default additionalDetailsSlice.reducer; 